<template>
    <div style="position: relative;">
        <div class="refresh">
            <el-button-group>
                <el-button @click="refresh(true)" icon="Refresh" :loading="loading"></el-button>
                <el-popover placement="top" :width="60" popper-class="custom-popover">
                    <div>
                        <el-button text @click="reload()">重新载入</el-button>
                    </div>
                    <div>
                        <el-button text @click="showLog()">显示日志</el-button>
                    </div>
                    <template #reference>
                        <el-button icon="Setting"></el-button>
                    </template>
                </el-popover>
            </el-button-group>
        </div>
        <el-table :data="list" empty-text="暂无仓位" v-loading="loading">
            <el-table-column prop="symbol" label="交易对" align="center" width="180"></el-table-column>
            <el-table-column label="仓位模式" align="center" width="120">
                <template #default="{ row }">
                    <el-tag v-show="row.marginType === constants.CROSS"
                        :type="row.positionSide === constants.SHORT ? 'danger' : 'success'"
                        style="width:80px; text-align:center">全仓
                        ×
                        {{ row.leverage }}</el-tag>
                    <el-tag v-show="row.marginType !== constants.CROSS"
                        :type="row.positionSide === constants.SHORT ? 'danger' : 'success'"
                        style="width:80px; text-align:center">逐仓
                        × {{ row.leverage }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="开仓均价" prop="entryPrice1" align="center" width="100"></el-table-column>
            <el-table-column label="数量" align="center" width="100">
                <template #default="{ row }">
                    {{ new Decimal(row.positionAmt).toDecimalPlaces(4, Decimal.ROUND_DOWN).toNumber() }}
                </template>
            </el-table-column>
            <el-table-column label="保证金" align="center" width="100">
                <template #default="{ row }">
                    {{ new Decimal(row.isolatedWallet).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber() }}
                </template>
            </el-table-column>
            <el-table-column label="参考强平价" prop="liquidationPrice1" align="center" width="100"></el-table-column>
            <el-table-column prop="markPrice1" label="标记价格" align="center" width="130"></el-table-column>
            <el-table-column label="收益额" align="center" width="100">
                <template #default="{ row }">
                    <span :class="Number(row.unRealizedProfit) < 0 ? 'red' : 'green'">
                        {{ new Decimal(row.unRealizedProfit).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber() }}
                    </span>
                </template>
            </el-table-column>
            <el-table-column label="" width="60" align="center">
                <template #default="{ row }">
                    <el-button plain @click="closePosition(row)" size="small">平仓</el-button>
                </template>
            </el-table-column>
            <el-table-column label="" width="50" align="center">
                <template #default="{ row }">
                    <CancelOrder :row="row" />
                </template>
            </el-table-column>
            <el-table-column label="">
                <template #default="{ row }">
                    <el-tag type="info" class="stopprofit" @click="takeStopProfit(row)">
                        <span class="green"> {{ row.tpTriggerPx || '--' }}</span>&nbsp;
                        <span class="red"> {{ row.slTriggerPx || '--' }}</span>
                    </el-tag>
                </template>
            </el-table-column>
        </el-table>
        <TakeStopProfit ref="TakeStopProfitRef" />
        <ClosePosition ref="ClosePositionRef" />
    </div>
</template>
<style scoped>
.refresh {
    position: absolute;
    top: -50px;
    right: 10px;
    height: 30px;
    z-index: 100;
}

.stopprofit {
    min-width: 65px;
    text-align: center;
    cursor: pointer;
}

.green {
    color: #67c23a;
}

.red {
    color: #ff707e;
}
</style>
<script setup>
import { ref, toRaw, inject, onMounted, onUnmounted } from "vue"
import { ElMessage } from 'element-plus'
import { useSystemStore } from '@/stores/system'
import { useLogStore } from '@/stores/log'
import * as constants from '@/utils/binanceConstants'
import Decimal from 'decimal.js'
import CancelOrder from "@/components/crypto/binance/positions/CancelOrder"
import TakeStopProfit from "@/components/crypto/binance/positions/TakeStopProfit"
import ClosePosition from "@/components/crypto/binance/positions/ClosePosition"
import WebSocketManager from "@/utils/binanceWebSocket"

const pannelActions = inject("pannelActions")
const systemStore = useSystemStore()
const logStore = useLogStore()
const loading = ref(false)
const list = ref([])
const orders = ref([])
const algoOrders = ref([])
const listenKey = ref("")
const TakeStopProfitRef = ref(null)
const ClosePositionRef = ref(null)

// 存储定时器 ID
let intervalId = null
let positionWS = null
let markPriceWS = null
let refreshTimer = null
let isRefreshing = false
let lastUpdateTime = 0
let lastRefreshTime = 0
const UPDATE_INTERVAL = 500
const REFRESH_INTERVAL = 2000
const REFRESH_DELAY = 500
const REFRESH_FOLLOW_UP_DELAY = 1500

// 组件卸载时关闭 WebSocket
onUnmounted(() => {
    markPriceWS?.close()
    positionWS?.close()
    intervalId && clearInterval(intervalId)
    refreshTimer && clearTimeout(refreshTimer)
})
// 添加健康检查（可选）
let healthMarkPriceWSInterval = null

onMounted(async () => {
    await initWebSocket()
    await getAllOrders()
    await getPositions()
})

const initWebSocket = async () => {
    await getListenKey()

    // ========================
    // 公共 WS（mark price）
    // ========================
    markPriceWS = new WebSocketManager("wss://fstream.binance.com/market/stream?streams=!markPrice@arr@1s")
    markPriceWS.on("onMessage", (data) => {
        updateMarkPrice(JSON.parse(data).data)
    })
    markPriceWS.connect()

    // ========================
    // 私有 WS（持仓）
    // ======================== 
    positionWS = new WebSocketManager(`wss://fstream.binance.com/private/ws?listenKey=${listenKey.value}`)
    positionWS.on("onMessage", (data) => {
        const message = JSON.parse(data)
        //仓位变化和止盈止损变化将触发
        if (message.e === "ACCOUNT_UPDATE" || message.e === "ORDER_TRADE_UPDATE" || message.e === "ALGO_UPDATE") {
            scheduleRefresh()
        }
    })
    positionWS.on("onOpen", () => {
        ElMessage.success("WebSocket 连接成功！")
    })
    positionWS.connect()

    refreshListenKey()
}

const refresh = async (isload, options = {}) => {
    const { force = false, followUp = true } = options

    if (isRefreshing) {
        if (!isload) scheduleRefresh(300, options)
        return
    }

    // Rate limit websocket refreshes, but keep one pending refresh queued.
    if (!isload && !force) {
        const now = Date.now()
        const remaining = REFRESH_INTERVAL - (now - lastRefreshTime)

        if (remaining > 0) {
            scheduleRefresh(remaining, { force: true, followUp })
            return
        }
    }

    if (!isload) lastRefreshTime = Date.now()

    if (isload) loading.value = true
    isRefreshing = true

    try {
        await getAllOrders()
        await getPositions()
    } finally {
        isRefreshing = false
        if (isload) loading.value = false
    }

    if (!isload && followUp) {
        scheduleRefresh(REFRESH_FOLLOW_UP_DELAY, { force: true, followUp: false })
    }
}

const scheduleRefresh = (delay = REFRESH_DELAY, options = {}) => {
    refreshTimer && clearTimeout(refreshTimer)

    refreshTimer = setTimeout(() => {
        refreshTimer = null
        refresh(false, options)
    }, delay)
}

const reload = () => {
    pannelActions.reloadPositions()
}

const showLog = () => {
    pannelActions.showLog()
}

const getListenKey = async () => {
    try {
        const data = await systemStore.privateClient.getListenKey()
        listenKey.value = data.listenKey
    } catch (error) {
        ElMessage.error('获取 listenKey 失败: ' + error.message)
        console.error('getListenKey error:', error)
        logStore.addLog({
            title: '获取 listenKey 失败',
            level: 'error',
            message: error
        })
    }
}

const updateMarkPrice = (array) => {
    const now = Date.now()
    // 添加：频率限制，避免过于频繁的更新
    if (now - lastUpdateTime < UPDATE_INTERVAL) {
        return
    }
    lastUpdateTime = now

    list.value.map(e => {
        let data = array.find(x => x.s === e.symbol)
        // console.log("更新标记价格",data)
        if (!data) return
        let s = systemStore.usymbols.find(x => x.symbol === e.symbol)

        let pricePrecision = parseInt(s.info.pricePrecision)
        e.markPrice = data.p ? parseFloat(data.p) : 0
        e.markPrice1 = data.p ? new Decimal(data.p).toDecimalPlaces(pricePrecision, Decimal.ROUND_DOWN).toNumber() : 0
        // 使用高精度计算未实现收益
        e.unRealizedProfit = new Decimal(e.markPrice1)
            .minus(e.entryPrice)
            .times(e.positionAmt)
            .toNumber()
        return e
    })
}

// 每 30 分钟刷新 listenKey
const refreshListenKey = () => {
    intervalId = setInterval(async () => {
        try {
            await systemStore.privateClient.keepAliveListenKey()
        } catch (e) {
            console.error("listenKey 保活失败，重新获取")
            await getListenKey()
            // 🔥 关键：重建 WS
            positionWS?.close()
            positionWS = new WebSocketManager(`wss://fstream.binance.com/private/ws?listenKey=${listenKey.value}`)
            positionWS.connect()
        }
    }, 30 * 60 * 1000)
}

const getPositions = async () => {
    try {
        let result = await systemStore.privateClient.positionsV2()

        result = result.filter(x => Number(x.positionAmt) !== 0)

        result.forEach(e => {
            let s = systemStore.usymbols.find(x => x.symbol === e.symbol)
            let pricePrecision = parseInt(s.info.pricePrecision)

            e.entryPrice1 = e.entryPrice ? new Decimal(e.entryPrice).toDecimalPlaces(pricePrecision, Decimal.ROUND_DOWN).toNumber() : 0
            e.markPrice1 = e.markPrice ? new Decimal(e.markPrice).toDecimalPlaces(pricePrecision, Decimal.ROUND_DOWN).toNumber() : 0
            e.liquidationPrice1 = e.liquidationPrice ? new Decimal(e.liquidationPrice).toDecimalPlaces(pricePrecision, Decimal.ROUND_DOWN).toNumber() : 0

            //获取止盈止损订单
            let tp = orders.value.find(x => x.symbol === e.symbol && x.positionSide == e.positionSide && x.type === constants.TAKE_PROFIT_MARKET)
            let sl = orders.value.find(x => x.symbol === e.symbol && x.positionSide == e.positionSide && x.type === constants.STOP_MARKET)

            let algoTp = algoOrders.value.find(x => x.symbol === e.symbol && x.positionSide == e.positionSide && x.orderType === constants.TAKE_PROFIT_MARKET)
            let algoSl = algoOrders.value.find(x => x.symbol === e.symbol && x.positionSide == e.positionSide && x.orderType === constants.STOP_MARKET)

            e.tpOrderId = tp ? tp.orderId : ""
            e.tpAlgoId = algoTp ? algoTp.algoId : ""
            e.tpTriggerPx = tp ? tp.stopPrice : (algoTp ? algoTp.triggerPrice : "")
            e.tpWorkingType = tp ? tp.workingType : (algoTp ? algoTp.workingType : "")

            e.slOrderId = sl ? sl.orderId : ""
            e.slAlgoId = algoSl ? algoSl.algoId : ""
            e.slTriggerPx = sl ? sl.stopPrice : (algoSl ? algoSl.triggerPrice : "")
            e.slWorkingType = sl ? sl.workingType : (algoSl ? algoSl.workingType : "")
        })

        list.value = result
    } catch (error) {
        ElMessage.error('获取仓位失败: ' + error.message)
        console.error('getPositions error:', error)
        logStore.addLog({
            title: '获取仓位失败',
            level: 'error',
            message: error
        })
    }
}

const getAllOrders = async () => {
    try {
        const results = await Promise.allSettled([
            systemStore.privateClient.openOrders(),
            systemStore.privateClient.openAlgoOrders()
        ])

        const [ordersResult, algoOrdersResult] = results

        if (ordersResult.status === 'fulfilled') {
            orders.value = ordersResult.value
        } else {
            console.error('获取普通订单失败', ordersResult.reason)
            ElMessage.warning('获取普通订单失败')
        }

        if (algoOrdersResult.status === 'fulfilled') {
            algoOrders.value = algoOrdersResult.value
        } else {
            console.error('获取条件单失败', algoOrdersResult.reason)
            ElMessage.warning('获取条件单失败')
        }
    } catch (error) {
        ElMessage.error('获取订单信息失败: ' + error.message)
        console.error('getAllOrders error:', error)
        logStore.addLog({
            title: '获取订单信息失败',
            level: 'error',
            message: error
        })
    }
}

const closePosition = (data) => {
    ClosePositionRef.value.init(data)
}

const takeStopProfit = (data) => {
    TakeStopProfitRef.value.init(data)
}

</script>
